<!-- eslint-disable vue-a11y/click-events-have-key-events -->

<!-- Shows the users' wishlists -->
<template>
  <div>
    <!-- Create wishlist modal -->
    <WishlistsDetailModal
      :show-modal.sync="showCreatemodal"
      :keep-body-modal-class="keepBodyModalClassDetail"
      @wishlistAdded="onWishlistAdded"
      @onClose="onDetailModalClosed"
    />

    <Modal
      :is-visible="showModalInternal"
      title="Add to wishlist"
      wrapper-class="modal-wishlists"
      :keep-body-modal-class="keepBodyModalClass"
      @onClose="close"
    >
      <template #body>
        <div class="container-fluid py-3">
          <!-- Create wishlist button -->
          <div class="row mb-3 row-wishlist rounded">
            <div class="col-12 d-flex position-relative p-1" @click="openCreateModal">
              <div class="img-wishlist">
                <font-awesome-icon
                  icon="plus"
                  size="2x"
                />
              </div>

              <!-- wishlist info -->
              <div class="d-flex flex-column justify-content-center ml-3 ">
                <div>Create a new wishlist</div>
              </div>
            </div>
          </div>

          <!-- Lists -->
          <div
            v-for="item in wishlists"
            :key="item.id"
            class="row mb-3 row-wishlist rounded"
            :class="{'row-selected-wishlist': item.id === selectedWishlistId}"
            @click="selectList(item)"
          >
            <div class="col-12 d-flex position-relative p-1">
              <div class="img-wishlist">
                <img
                  class="img-fluid"
                  :src="item.img ? item.img : PlaceholderImage"
                  :alt="item.name"
                >
              </div>

              <!-- wishlist info -->
              <div class="d-flex flex-column justify-content-center ml-3 ">
                <div>{{ item.name }}</div>
                <div class="wishlist-info">
                  <font-awesome-icon
                    :icon="['fas', item.is_public ? 'globe-americas' : 'lock']"
                  />
                  <span class="ml-1">{{ getNumberOfItemsLabel(item) }}</span>
                </div>
              </div>

              <!-- selected icon -->
              <font-awesome-icon
                v-if="item.id === selectedWishlistId"
                :icon="['fas', 'check']"
                class="icon-selected text-secondary"
              />
            </div>
          </div>
        </div>
      </template>

      <template #footer>
        <button
          type="button"
          class="btn btn-outline-secondary mr-3"
          @click.prevent="close"
        >
          Cancel
        </button>
        <button
          type="button"
          class="btn btn-primary"
          :disabled="isProcessing || wishlists.length === 0"
          @click.prevent="done"
        >
          Save
        </button>
      </template>
    </Modal>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import WishlistsDetailModal from './DetailModal.vue';
import PlaceholderImage from '~/static/image-placeholder.svg';
import Modal from '~/components/common/Modal.vue';

export default {
  components: {
    Modal,
    FontAwesomeIcon,
    WishlistsDetailModal,
  },
  props: {
    showModal: {
      type: Boolean,
      required: true,
      default: false,
    },

    dealId: {
      type: Number,
      required: true,
    },
  },

  data() {
    return {
      selectedWishlist: null,
      selectedWishlistId: 0,

      // Stores original wishlist id, set again user closes the modal
      // and didn't select a new wishlist
      selectedWishlistIdCopy: 0,

      // Stores the relashionship id between deals and wishlists,
      // this is used to remove a deal from a wishlist.
      wishlistDealId: 0,

      // For internal use, this should be sync with the showModal property
      showModalInternal: false,
      PlaceholderImage,

      // Create modal
      showCreatemodal: false,

      // Prevent weird behaviour when opening the create modal after closing this one
      keepBodyModalClass: false,
      keepBodyModalClassDetail: false,
    };
  },

  computed: {
    // Get the wishlist object that the deal belongs to, and also assigns wishlistDealId
    wishlist() {
      return this.wishlists.find(
        (x) => x.deals.find((y) => {
          if (y.id === this.dealId) {
            this.wishlistDealId = y.wishlist_deal_id;
            return true;
          }

          return null;
        }),
      ) || {};
    },

    // get all wishlists
    wishlists() {
      return this.$store.getters['wishlists/wishlists'];
    },

    // Deal is already on a wishlist
    dealIsInWishlist() {
      return !!this.selectedWishlistIdCopy;
    },

    isRemovingFromWishlist() {
      return this.dealIsInWishlist && this.selectedWishlistId === 0;
    },

    requestErrorMessage() {
      return this.$store.getters['wishlists/errorMessage'];
    },

    isProcessing() {
      return this.$store.getters['wishlists/isProcessing'];
    },

    // Order asc and get latest element based on the id
    latestWishlist() {
      const wishlists = [...this.wishlists];
      return [...wishlists.sort((a, b) => a.id - b.id)].pop();
    },
  },

  watch: {
    showModal(newValue) {
      // Refresh wishlists when opening the modal
      // Once the cache issue is fixed I think we can remove this
      if (newValue) {
        this.fetchWishlists();
      }

      // Sync internal variable
      this.showModalInternal = newValue;
    },
  },

  methods: {
    async fetchWishlists() {
      await this.$store.dispatch('wishlists/getWishlists');

      if (this.requestErrorMessage && this.$toastr) {
        this.$toastr.e(this.requestErrorMessage);
        return;
      }

      this.wishlists = this.$store.getters['wishlists/wishlists'];
      this.selectedWishlist = this.wishlist;
      this.selectedWishlistId = this.selectedWishlist.id;
      this.selectedWishlistIdCopy = this.selectedWishlistId;
    },

    close() {
      this.showModalInternal = false;
      this.selectedWishlistId = this.selectedWishlistIdCopy;

      this.$emit('update:showModal', this.showModalInternal);
    },

    onDetailModalClosed() {
      // Close this if create modal is closed
      this.keepBodyModalClass = false;
      this.keepBodyModalClassDetail = false;
      this.showModalInternal = false;

      this.$emit('update:showModal', this.showModalInternal);
    },

    async onWishlistAdded() {
      await this.fetchWishlists();

      this.selectedWishlist = this.latestWishlist;
      this.selectedWishlistId = this.latestWishlist.id;

      // Close create modal and open this if a new list is added
      this.showCreatemodal = false;
      this.showModalInternal = true;
      this.keepBodyModalClass = false;
    },

    done() {
      // No new wishlist selected then close modal
      if (
        (!this.selectedWishlistIdCopy && !this.selectedWishlistId)
          || this.selectedWishlistIdCopy === this.selectedWishlistId
      ) {
        this.close();
        return;
      }

      if (this.isRemovingFromWishlist) {
        this.removeFromList();
      } else {
        this.saveList();
      }
    },

    async removeFromList() {
      if (this.isProcessing) {
        return;
      }

      await this.$store.dispatch('wishlists/removeFromList', { wishlistDealId: this.wishlistDealId });

      if (this.requestErrorMessage) {
        this.$toastr.e(this.requestErrorMessage);
        return;
      }

      await this.$store.dispatch('wishlists/setWishlist', null);

      this.$toastr.s(`Removed from <strong>${this.wishlist.name}</strong> wishlist!`);

      const wishlistsCount = this.$store.getters['wishlists/wishlistsCount'];

      // If deal is in a wishlist reduce counter
      if (this.isRemovingFromWishlist) {
        await this.$store.dispatch('wishlists/setWishlistCount', { value: wishlistsCount - 1 });
      }

      this.showModalInternal = false;
      this.$emit('update:showModal', this.showModalInternal);

      // Update modal
      await this.fetchWishlists();
    },

    selectList(wishlist) {
      if (this.selectedWishlistId && this.selectedWishlistId === wishlist.id) {
        this.selectedWishlistId = 0;
        return;
      }

      this.selectedWishlist = wishlist;
      this.selectedWishlistId = this.selectedWishlist.id;
    },

    async saveList() {
      if (this.isProcessing) {
        return;
      }

      const wishlistsCount = this.$store.getters['wishlists/wishlistsCount'];

      const requestData = {
        // Used to remove from previous deal
        removeFromList: this.dealIsInWishlist,
        wishlistDealId: this.wishlistDealId,

        // Used to add add to new wishlist
        user: this.$store.state.session.user.id,
        wishlist: this.selectedWishlistId,
        deal: this.dealId,
      };

      await this.$store.dispatch('wishlists/saveToList', requestData);

      if (this.requestErrorMessage) {
        this.$toastr.e(this.requestErrorMessage);
        return;
      }

      // If deal is added to a new list then update state
      if (!this.dealIsInWishlist) {
        await this.$store.dispatch('wishlists/setWishlistCount', { value: wishlistsCount + 1 });
      }

      await this.$store.dispatch('wishlists/setWishlist', this.selectedWishlist);

      this.$toastr.s(`Saved to <strong>${this.selectedWishlist.name}</strong> wishlist!`);

      this.showModalInternal = false;
      this.$emit('update:showModal', this.showModalInternal);

      // Update modal
      await this.fetchWishlists();
    },

    openCreateModal() {
      this.keepBodyModalClass = true;
      this.keepBodyModalClassDetail = true;
      this.showModalInternal = false;
      this.showCreatemodal = true;
    },

    getNumberOfItemsLabel(item) {
      const { length } = item.deals;
      const label = length === 1 ? ' item' : ' items';

      return length + label;
    },
  },
};
</script>

<style scoped lang="scss">
@import '~/styles/dorado/main';

/**
 * Override legacy styles from `project.css`. This should be removed/updated
 * once dorado is implemented.
 *
 * Also, we would need to remove the div element wrapping this component.
 */
div ::v-deep {
  .modal-dialog-scrollable {
    max-height: calc(100% - 7rem);
  }

  .modal-wishlists .modal-dialog {
    left: 0;
    margin-top: 7rem;
    position: relative;
    top: 0;
  }

  .modal {
    z-index: 9998; // mimic other modal indexes
  }

  .modal-backdrop {
    z-index: 9997;
  }

  .modal-body {
    box-shadow: none;
  }
}

.btn {
  border-radius: 40px;
  padding: 8px 24px;
}

.img-wishlist {
  align-items: center;
  background-position: center;
  background-size: cover;
  border: 1px solid #dadde5;
  display: flex;
  height: 60px;
  justify-content: center;
  overflow: hidden;
  width: 60px;
}

.icon-selected {
  font-size: 1.3rem;
  position: absolute;
  right: 15px;
  top: 20px;
}

// TODO: Update with dorado class later
.wishlist-info {
  color: #434d59;
}

.row-wishlist {
  cursor: pointer;
  transition: ease 0.2s;

  &:hover {
    background-color: #f1f1f1;
  }
}

.row-selected-wishlist {
  // TODO: Update with dorado color
  background-color: #f5f5f9;
}
</style>
